import React from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import actions from '../../profilePageActions';
import {
  NUM_OF_REVIEWS_IN_PROFILE_SECTION,
  PageWidthBreakPoint,
} from '../../../../utils/constants';
import isServer from '../../../../utils/isServer';
import ReviewsSummary from './ReviewsSummary';
import ReviewItem from './ReviewItem/ReviewItem';
import ReviewItemMobile from './ReviewItem/ReviewItemMobile';
import ReplyReviewModalLayoutDesktop from './ReviewsModal/ReplyReviewModalLayoutDesktop';
import ReplyReviewModalLayoutMobile from './ReviewsModal/ReplyReviewModalLayoutMobile';
import ReviewStars from '../../../common/ReviewStars/ReviewStars';
import s from './ExpertDataReviews.scss';
import { Heading, TextButton, Modal } from 'wix-style-react';
import ChevronDown from 'wix-ui-icons-common/ChevronDown';
import { ServicesProvider } from '@wix/experts-common';

class ExpertDataReviews extends React.Component {
  constructor(props) {
    super(props);
    this.replyBody = '';
    this.replyToReviewInProgress = false;
    this.isMatchMedia1024 = isServer
      ? true
      : window.matchMedia(PageWidthBreakPoint.Width_1024).matches;

    this.state = {
      showMore: true,
      displayedReviews: [],
      showReplyToReviewModal: false,
      selectedReview: null,
      disableReplyButton: this.shouldDisableReplyButton(),
    };
  }

  static propTypes = {
    t: PropTypes.func,
    expertGuid: PropTypes.string,
    reviews: PropTypes.array,
    reviewScores: PropTypes.object,
    expertDetails: PropTypes.object,
    isMyProfile: PropTypes.bool,
  };

  static defaultProps = {
    isMyProfile: false,
  };

  componentDidMount() {
    this.onShowMoreClicked();
  }

  componentDidUpdate(prevProps) {
    if (this.props.reviews !== prevProps.reviews) {
      this.setState(
        {
          displayedReviews: [],
        },
        this.onShowMoreClicked,
      );
    }
  }

  shouldDisableReplyButton = () => this.replyBody.length === 0;

  onShowMoreClicked = shouldSendBi => {
    shouldSendBi &&
      this.context.services.withBI({
        evid: 149,
        typeid: 1,
        proGuid: this.props.expertGuid,
        reviewsScore: JSON.stringify(this.props.reviewScores),
      });
    let showMore = true;
    if (
      this.props.reviews &&
      this.state.displayedReviews.length < this.props.reviews.length
    ) {
      const lastReviewItemIndex =
        this.state.displayedReviews.length + NUM_OF_REVIEWS_IN_PROFILE_SECTION <
        this.props.reviews.length
          ? this.state.displayedReviews.length +
            NUM_OF_REVIEWS_IN_PROFILE_SECTION
          : this.props.reviews.length;
      showMore = lastReviewItemIndex < this.props.reviews.length;
      this.setState({
        displayedReviews: this.state.displayedReviews.concat(
          this.props.reviews.slice(
            this.state.displayedReviews.length,
            lastReviewItemIndex,
          ),
        ),
        showMore,
      });
    }
  };

  onReplyReviewClicked = review => {
    this.context.services.withBI({
      evid: 170,
      typeid: 1,
      jobRequestId: review.completedData.jobRequestId,
    });
    this.setState({ showReplyToReviewModal: true, selectedReview: review });
  };

  replyToReview = async () => {
    this.replyToReviewInProgress = true;
    const replyResponse = await this.props.addReplyToReview(
      this.state.selectedReview.completedData.jobRequestId,
      this.replyBody,
    );
    this.context.services.withBI({
      evid: 171,
      typeid: 1,
      jobRequestId: this.state.selectedReview.completedData.jobRequestId,
    });
    this.replyToReviewInProgress = false;
    if (replyResponse.statusText === 'OK') {
      this.closeReplyModal();
    }
  };

  closeReplyModal = () => {
    if (!this.state.replyToReviewInProgress) {
      this.replyBody = '';
      this.setState({
        showReplyToReviewModal: false,
        disableReplyButton: this.shouldDisableReplyButton(),
        selectedReview: null,
      });
    }
  };

  render() {
    const { t, reviewScores, isMyProfile, expertDetails } = this.props;
    const {
      showMore,
      displayedReviews,
      showReplyToReviewModal,
      selectedReview,
      disableReplyButton,
    } = this.state;
    const { isInModal } = this.context.services;

    const getDisplayedReviews = displayedReviews.map((review, index) => {
      const onReplyReviewClicked =
        isMyProfile && !isInModal && !review.reviewData.review.reply
          ? this.onReplyReviewClicked
          : null;
      return this.isMatchMedia1024 ? (
        <div className={s.reviewItemContainer} key={index}>
          <ReviewItem
            review={review}
            onReplyReviewClicked={onReplyReviewClicked}
          />
        </div>
      ) : (
        <div className={s.reviewItemContainer} key={index}>
          <ReviewItemMobile
            review={review}
            onReplyReviewClicked={onReplyReviewClicked}
          />
        </div>
      );
    });

    return (
      <div className={s.expertDataReviews}>
        {showReplyToReviewModal && (
          <Modal
            dataHook="reply-to-review-modal"
            isOpen={showReplyToReviewModal}
          >
            {this.isMatchMedia1024 ? (
              <ReplyReviewModalLayoutDesktop
                closeReplyModal={this.closeReplyModal}
                replyToReview={this.replyToReview}
                disableReplyButton={disableReplyButton}
                expertDetails={expertDetails}
                selectedReview={selectedReview}
                onInputAreaChange={e => {
                  this.replyBody = e.target.value;
                  this.setState({
                    disableReplyButton: this.shouldDisableReplyButton(),
                  });
                }}
              />
            ) : (
              <ReplyReviewModalLayoutMobile
                closeReplyModal={this.closeReplyModal}
                replyToReview={this.replyToReview}
                disableReplyButton={disableReplyButton}
                selectedReview={selectedReview}
                onInputAreaChange={e => {
                  this.replyBody = e.target.value;
                  this.setState({
                    disableReplyButton: this.shouldDisableReplyButton(),
                  });
                }}
              />
            )}
          </Modal>
        )}
        <div className={s.title} data-hook="expert-data-reviews-title">
          <Heading appearance="H2">
            {t('client-expertview-reviews-title', {
              count: reviewScores.count,
            })}
          </Heading>
          &nbsp;&nbsp;
          <ReviewStars fullStars={reviewScores.score} size="medium" />
        </div>
        <div className={s.expertDataReviews}>
          <ReviewsSummary reviewScores={reviewScores} />
        </div>
        <div className={s.content}>
          <div className={s.text} data-hook="displayed-reviews">
            {getDisplayedReviews}
          </div>
          {showMore && (
            <TextButton
              className={s.showMoreReviews}
              dataHook="show-more-reviews"
              onClick={() => this.onShowMoreClicked(true)}
              skin="dark"
              weight="normal"
              suffixIcon={<ChevronDown />}
            >
              {t('client-expertview-showmore')}
            </TextButton>
          )}
        </div>
      </div>
    );
  }
}

ExpertDataReviews.contextTypes = ServicesProvider.childContextTypes;
const ConnectedExpertDataReviews = connect(() => ({}), { ...actions })(
  ExpertDataReviews,
);
export default translate()(ConnectedExpertDataReviews);
